import { useEffect, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import { puedeModoGlobal } from "../../utils/rolesModoGlobal";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  AccountBalance,
  AttachMoney,
  CheckCircle,
  Clear,
  Close,
  CreditCard,
  DirectionsCar,
  FilterList,
  History,
  LocalAtm,
  Nightlife,
  Person,
  PhoneAndroid,
  Receipt,
  Schedule,
  SwapHoriz,
} from "@mui/icons-material";
import apiClient from "../../utils/axios";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("es-EC", {
    timeZone: "America/Guayaquil",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
};

const hoy = () => new Date().toISOString().slice(0, 10);

const METODOS = [
  { key: "total_efectivo",      label: "Efectivo",           icon: <LocalAtm />,       color: "#22c55e" },
  { key: "total_tarjeta",       label: "Tarjeta",            icon: <CreditCard />,     color: "#3b82f6" },
  { key: "total_transferencia", label: "Transferencia",      icon: <AccountBalance />, color: "#8b5cf6" },
  { key: "total_de_una",        label: "De Una",             icon: <PhoneAndroid />,   color: "#F59E0B" },
  { key: "total_otro",          label: "Otro método de pago",icon: <SwapHoriz />,      color: "#ec4899" },
  { key: "total_sin_metodo",    label: "Sin método",         icon: <Receipt />,        color: "#ef4444" },
];

// ── Tarjeta de total por método ───────────────────────────────────────────────

const TarjetaMetodo = ({ label, icon, color, valor }) => (
  <Card variant="outlined" sx={{ borderLeft: `4px solid ${color}`, height: "100%" }}>
    <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 } }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, color }}>
        {icon}
        <Typography variant="caption" fontWeight={700} color="text.secondary">
          {label.toUpperCase()}
        </Typography>
      </Box>
      <Typography variant="h5" fontWeight={800}>{fmt(valor)}</Typography>
    </CardContent>
  </Card>
);

// ── Componente principal ──────────────────────────────────────────────────────

const FILTROS_INIT = { fechaDesde: "", fechaHasta: "", operador: "" };

const CierreCaja = ({ open, onClose, soloTurno = false }) => {
  const { user } = useAuth();
  const [resumen, setResumen]       = useState(null);
  const [historial, setHistorial]   = useState([]);
  const [total, setTotal]           = useState(0);
  const [loading, setLoading]       = useState(false);
  const [loadingHist, setLoadingHist] = useState(false);
  const [cerrando, setCerrando]     = useState(false);
  const [observaciones, setObservaciones] = useState("");
  const [confirmar, setConfirmar]   = useState(false);
  const [snackbar, setSnackbar]     = useState({ open: false, message: "", severity: "success" });
  const [vista, setVista]           = useState("turno");

  // Filtros
  const [filtros, setFiltros]       = useState(FILTROS_INIT);
  const [filtrosActivos, setFiltrosActivos] = useState(FILTROS_INIT);

  // Sucursal: solo elegible en modo global (sin sucursal fija). '' (string
  // vacío) = "mi contexto actual" (no se manda el parámetro, comportamiento
  // de siempre). 'global' = todo el parqueadero combinado. Un id = esa
  // sucursal puntual — permite cerrar/ver su caja sin entrar a ella.
  const [sucursalSel, setSucursalSel] = useState("");
  const [sucursales, setSucursales]   = useState([]);
  const enModoGlobal = puedeModoGlobal(user) && user?.sucursal_id == null;

  useEffect(() => {
    if (!enModoGlobal || !open) return;
    apiClient.get("/api/sucursales")
      .then(res => setSucursales(res.data?.data || []))
      .catch(() => setSucursales([]));
  }, [enModoGlobal, open]);

  const paramsSucursal = () => {
    if (sucursalSel === "") return {};
    if (sucursalSel === "global") return { sucursal_id: "" };
    return { sucursal_id: sucursalSel };
  };

  const mostrarSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  // ── Turno actual ──────────────────────────────────────────────────────────
  const cargarTurno = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/api/cierres-caja/turno-actual", { params: paramsSucursal() });
      if (res?.data?.success) setResumen(res.data.data);
    } catch {
      mostrarSnackbar("Error cargando turno actual", "error");
    } finally {
      setLoading(false);
    }
  };

  // ── Historial con filtros ─────────────────────────────────────────────────
  const cargarHistorial = async (f = filtrosActivos) => {
    setLoadingHist(true);
    try {
      const params = new URLSearchParams({ limit: 100 });
      if (f.fechaDesde) params.set("fecha_desde", f.fechaDesde);
      if (f.fechaHasta) params.set("fecha_hasta", f.fechaHasta);
      if (f.operador)   params.set("operador", f.operador);
      const suc = paramsSucursal();
      if ("sucursal_id" in suc) params.set("sucursal_id", suc.sucursal_id);
      const res = await apiClient.get(`/api/cierres-caja?${params}`);
      if (res?.data?.success) {
        setHistorial(res.data.data);
        setTotal(res.data.total);
      }
    } catch {
      mostrarSnackbar("Error cargando historial", "error");
    } finally {
      setLoadingHist(false);
    }
  };

  useEffect(() => {
    if (open) {
      setVista("turno");
      setObservaciones("");
      setConfirmar(false);
      setFiltros(FILTROS_INIT);
      setFiltrosActivos(FILTROS_INIT);
      setSucursalSel("");
      cargarTurno();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Cuando cambia a historial, cargar con filtros actuales
  useEffect(() => {
    if (vista === "historial") cargarHistorial(filtrosActivos);
  }, [vista]);

  // Cambiar de sucursal recarga la vista activa con el nuevo contexto
  useEffect(() => {
    if (!open) return;
    if (vista === "turno") cargarTurno();
    else cargarHistorial(filtrosActivos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sucursalSel]);

  const aplicarFiltros = () => {
    setFiltrosActivos({ ...filtros });
    cargarHistorial(filtros);
  };

  const limpiarFiltros = () => {
    setFiltros(FILTROS_INIT);
    setFiltrosActivos(FILTROS_INIT);
    cargarHistorial(FILTROS_INIT);
  };

  const hayFiltros = Object.values(filtrosActivos).some(Boolean);

  const realizarCierre = async () => {
    setCerrando(true);
    try {
      const res = await apiClient.post("/api/cierres-caja", { observaciones, ...paramsSucursal() });
      if (res?.data?.success) {
        mostrarSnackbar("Cierre de caja registrado exitosamente");
        setConfirmar(false);
        setObservaciones("");
        await cargarTurno();
      }
    } catch (e) {
      mostrarSnackbar(e?.response?.data?.error || "Error al realizar el cierre", "error");
    } finally {
      setCerrando(false);
    }
  };

  // Totales de la tabla filtrada
  const sumHistorial = (key) =>
    historial.reduce((acc, c) => acc + Number(c[key] ?? 0), 0);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
        PaperProps={{ sx: { borderRadius: 4, overflow: "hidden" } }}
      >
        {/* Header */}
        <DialogTitle sx={{ backgroundColor: "#282626", color: "white", p: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SwapHoriz />
              <Box>
                <Typography variant="h6" fontWeight={700}>Cambio de Turno</Typography>
                <Typography variant="caption" sx={{ opacity: 0.75 }}>
                  Cierre de caja y resumen de ingresos
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              {enModoGlobal && (
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <Select
                    value={sucursalSel}
                    onChange={(e) => setSucursalSel(e.target.value)}
                    displayEmpty
                    sx={{
                      color: "white", bgcolor: "rgba(255,255,255,0.08)", borderRadius: 2,
                      "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.3)" },
                      "& .MuiSvgIcon-root": { color: "white" },
                    }}
                  >
                    <MenuItem value="">Mi contexto actual</MenuItem>
                    <MenuItem value="global">Todo el parqueadero</MenuItem>
                    {sucursales.map(s => (
                      <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
              {!soloTurno && (
                <>
                  <Tooltip title="Turno actual">
                    <Chip label="Turno actual" icon={<Schedule />} size="small"
                      onClick={() => setVista("turno")}
                      sx={{ cursor: "pointer", bgcolor: vista === "turno" ? "rgba(255,255,255,0.2)" : "transparent", color: "white", border: "1px solid rgba(255,255,255,0.3)" }} />
                  </Tooltip>
                  <Tooltip title="Historial de cierres">
                    <Chip label="Historial" icon={<History />} size="small"
                      onClick={() => setVista("historial")}
                      sx={{ cursor: "pointer", bgcolor: vista === "historial" ? "rgba(255,255,255,0.2)" : "transparent", color: "white", border: "1px solid rgba(255,255,255,0.3)" }} />
                  </Tooltip>
                </>
              )}
              <IconButton onClick={onClose} size="small" sx={{ color: "white" }}>
                <Close />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 3, bgcolor: "#f8fafc" }}>
          {/* ── TURNO ACTUAL ── */}
          {vista === "turno" && (
            loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : (
              <>
                {resumen && (
                  <Alert severity="info" icon={<Schedule />} sx={{ mb: 2 }}>
                    <strong>Operador:</strong>{" "}
                    {[user?.name, user?.lastname].filter(Boolean).join(" ") || user?.username || "—"}
                    &nbsp;|&nbsp;
                    <strong>Período:</strong>{" "}
                    {fmtDateTime(resumen.fecha_inicio)} → {fmtDateTime(resumen.fecha_hasta)}
                    {enModoGlobal && (
                      <>
                        &nbsp;|&nbsp;<strong>Sucursal:</strong>{" "}
                        {sucursalSel === "" ? "Mi contexto actual"
                          : sucursalSel === "global" ? "Todo el parqueadero"
                            : sucursales.find(s => String(s.id) === String(sucursalSel))?.nombre || sucursalSel}
                      </>
                    )}
                  </Alert>
                )}

                <Grid container spacing={2} sx={{ mb: 2 }}>
                  {METODOS.filter(({ key }) => key !== "total_sin_metodo" || (resumen?.[key] ?? 0) > 0)
                    .map(({ key, label, icon, color }) => (
                      <Grid item xs={6} sm="auto" key={key} sx={{ flexGrow: 1 }}>
                        <TarjetaMetodo label={label} icon={icon} color={color} valor={resumen?.[key] ?? 0} />
                      </Grid>
                    ))}
                </Grid>

                {(resumen?.num_ticket_perdido ?? 0) > 0 && (
                  <Card variant="outlined" sx={{ borderLeft: "4px solid #dc2626", mb: 2, bgcolor: "#fff5f5" }}>
                    <CardContent sx={{ py: 1.5, px: 2, "&:last-child": { pb: 1.5 }, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Receipt sx={{ color: "#dc2626" }} fontSize="small" />
                        <Box>
                          <Typography variant="caption" fontWeight={700} color="text.secondary">TICKETS PERDIDOS</Typography>
                          <Typography variant="body2" color="text.secondary">{resumen.num_ticket_perdido} cobro(s) con cargo adicional</Typography>
                        </Box>
                      </Box>
                      <Typography variant="h6" fontWeight={800} color="#dc2626">
                        {fmt(resumen.monto_ticket_perdido)}
                      </Typography>
                    </CardContent>
                  </Card>
                )}

                {/* ── Resumen principal ── */}
                <Card elevation={0} sx={{ mb: 2, border: "1px solid #E5E5E5", borderRadius: 3, overflow: "hidden" }}>
                  {/* Total General — fila completa */}
                  <Box sx={{
                    bgcolor: "white", px: 3, py: 2.5,
                    borderBottom: "1px solid #F0F0F0",
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                  }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "rgba(245,158,11,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <AttachMoney sx={{ color: "#F59E0B", fontSize: 26 }} />
                      </Box>
                      <Box>
                        <Typography variant="caption" fontWeight={700} sx={{ color: "#888", textTransform: "uppercase", letterSpacing: 0.8 }}>
                          Total General
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#aaa" }}>Ingresos del turno</Typography>
                      </Box>
                    </Box>
                    <Typography variant="h3" fontWeight={800} sx={{ color: "#282626" }}>
                      {fmt(resumen?.total_general)}
                    </Typography>
                  </Box>

                  {/* Vehículos — dos columnas */}
                  <Grid container sx={{ bgcolor: "white", width: "100%", m: 0 }}>
                    <Grid item xs={6} sx={{ borderRight: "1px solid #F0F0F0" }}>
                      <Box sx={{ py: 4, px: 3, textAlign: "center" }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: 3, bgcolor: "rgba(40,38,38,0.06)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                          <DirectionsCar sx={{ fontSize: 30, color: "#282626" }} />
                        </Box>
                        <Typography variant="h2" fontWeight={800} sx={{ color: "#282626", lineHeight: 1 }}>
                          {resumen?.num_vehiculos_cobrados ?? 0}
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: "#888", mt: 1 }}>
                          Vehículos cobrados
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ py: 4, px: 3, textAlign: "center" }}>
                        <Box sx={{ width: 56, height: 56, borderRadius: 3, bgcolor: "rgba(34,197,94,0.08)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                          <CheckCircle sx={{ fontSize: 30, color: "#16a34a" }} />
                        </Box>
                        <Typography variant="h2" fontWeight={800} sx={{ color: "#282626", lineHeight: 1 }}>
                          {resumen?.num_vehiculos_gratis ?? 0}
                        </Typography>
                        <Typography variant="body1" fontWeight={600} sx={{ color: "#888", mt: 1 }}>
                          Vehículos gratis
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </Card>

                <TextField fullWidth size="small" label="Observaciones del turno (opcional)"
                  multiline rows={2} value={observaciones}
                  onChange={(e) => setObservaciones(e.target.value)} sx={{ mb: 1 }}
                />
              </>
            )
          )}

          {/* ── HISTORIAL ── */}
          {vista === "historial" && (
            <>
              {/* Panel de filtros */}
              <Card variant="outlined" sx={{ mb: 2, p: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <FilterList fontSize="small" color="action" />
                  <Typography variant="subtitle2" fontWeight={700}>Filtros</Typography>
                  {hayFiltros && (
                    <Chip label="Filtros activos" size="small" color="primary" variant="outlined" />
                  )}
                </Box>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth size="small" label="Desde" type="date"
                      InputLabelProps={{ shrink: true }}
                      value={filtros.fechaDesde}
                      onChange={(e) => setFiltros((f) => ({ ...f, fechaDesde: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <TextField
                      fullWidth size="small" label="Hasta" type="date"
                      InputLabelProps={{ shrink: true }}
                      value={filtros.fechaHasta}
                      onChange={(e) => setFiltros((f) => ({ ...f, fechaHasta: e.target.value }))}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth size="small" label="Operador"
                      placeholder="Nombre del operador..."
                      value={filtros.operador}
                      onChange={(e) => setFiltros((f) => ({ ...f, operador: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && aplicarFiltros()}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><Person fontSize="small" /></InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={2}>
                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button fullWidth variant="contained" size="small"
                        onClick={aplicarFiltros} disabled={loadingHist}
                        sx={{ bgcolor: "#F59E0B", "&:hover": { bgcolor: "#D97706" }, fontWeight: 700 }}
                      >
                        Buscar
                      </Button>
                      {hayFiltros && (
                        <Tooltip title="Limpiar filtros">
                          <IconButton size="small" onClick={limpiarFiltros}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </Grid>
                </Grid>

                {/* Accesos rápidos de fecha */}
                <Box sx={{ display: "flex", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
                  {[
                    { label: "Hoy",        desde: hoy(), hasta: hoy() },
                    { label: "Esta semana", desde: (() => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0,10); })(), hasta: hoy() },
                    { label: "Este mes",   desde: new Date().toISOString().slice(0, 7) + "-01", hasta: hoy() },
                    { label: "Último mes", desde: (() => { const d = new Date(); d.setMonth(d.getMonth()-1,1); return d.toISOString().slice(0,10); })(),
                                           hasta: (() => { const d = new Date(); d.setDate(0); return d.toISOString().slice(0,10); })() },
                  ].map(({ label, desde, hasta }) => (
                    <Chip key={label} label={label} size="small" variant="outlined" clickable
                      onClick={() => {
                        const nf = { ...filtros, fechaDesde: desde, fechaHasta: hasta };
                        setFiltros(nf);
                        setFiltrosActivos(nf);
                        cargarHistorial(nf);
                      }}
                      sx={{ cursor: "pointer" }}
                    />
                  ))}
                </Box>
              </Card>

              {/* Tabla */}
              {loadingHist ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : historial.length === 0 ? (
                <Box sx={{ textAlign: "center", py: 6 }}>
                  <History sx={{ fontSize: 56, color: "text.disabled", mb: 1 }} />
                  <Typography color="text.secondary">
                    {hayFiltros ? "Sin resultados para los filtros aplicados" : "Sin cierres registrados"}
                  </Typography>
                </Box>
              ) : (
                <>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: "block" }}>
                    {total} cierre{total !== 1 ? "s" : ""} encontrado{total !== 1 ? "s" : ""}
                  </Typography>
                  <Box sx={{ overflowX: "auto" }}>
                    <Table size="small" sx={{ minWidth: 700 }}>
                      <TableHead>
                        <TableRow sx={{ "& th": { fontWeight: 700, bgcolor: "#f1f5f9", whiteSpace: "nowrap" } }}>
                          <TableCell>Fecha cierre</TableCell>
                          <TableCell>Operador</TableCell>
                          <TableCell align="right">Efectivo</TableCell>
                          <TableCell align="right">Tarjeta</TableCell>
                          <TableCell align="right">Transf.</TableCell>
                          <TableCell align="right">De Una</TableCell>
                          <TableCell align="right">Otros</TableCell>
                          <TableCell align="right">Total</TableCell>
                          <TableCell align="center">Vehículos</TableCell>
                          <TableCell>Observaciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {historial.map((c) => (
                          <TableRow key={c.id} hover>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>
                              <Typography variant="body2">{fmtDateTime(c.fecha_cierre)}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                Desde: {fmtDateTime(c.fecha_inicio)}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{c.usuario_nombre}</TableCell>
                            <TableCell align="right">{fmt(c.total_efectivo)}</TableCell>
                            <TableCell align="right">{fmt(c.total_tarjeta)}</TableCell>
                            <TableCell align="right">{fmt(c.total_transferencia)}</TableCell>
                            <TableCell align="right">{fmt(c.total_de_una)}</TableCell>
                            <TableCell align="right">{fmt(c.total_otro)}</TableCell>
                            <TableCell align="right">{fmt(c.total_sin_metodo)}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={700}>{fmt(c.total_general)}</Typography>
                            </TableCell>
                            <TableCell align="center">{c.num_vehiculos}</TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {c.observaciones || "—"}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                      {/* Fila de totales */}
                      {historial.length > 1 && (
                        <TableFooter>
                          <TableRow sx={{ "& td": { fontWeight: 800, bgcolor: "#e2e8f0", borderTop: "2px solid #94a3b8" } }}>
                            <TableCell colSpan={2}>TOTAL ({historial.length} cierres)</TableCell>
                            <TableCell align="right">{fmt(sumHistorial("total_efectivo"))}</TableCell>
                            <TableCell align="right">{fmt(sumHistorial("total_tarjeta"))}</TableCell>
                            <TableCell align="right">{fmt(sumHistorial("total_transferencia"))}</TableCell>
                            <TableCell align="right">{fmt(sumHistorial("total_de_una"))}</TableCell>
                            <TableCell align="right">{fmt(sumHistorial("total_otro"))}</TableCell>
                            <TableCell align="right">{fmt(sumHistorial("total_sin_metodo"))}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={900} color="primary">{fmt(sumHistorial("total_general"))}</Typography>
                            </TableCell>
                            <TableCell align="center">{historial.reduce((a, c) => a + c.num_vehiculos, 0)}</TableCell>
                            <TableCell />
                          </TableRow>
                        </TableFooter>
                      )}
                    </Table>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={onClose} variant="outlined">Cerrar</Button>
          {vista === "turno" && (
            <Button variant="contained"
              startIcon={cerrando ? <CircularProgress size={16} color="inherit" /> : <SwapHoriz />}
              onClick={() => setConfirmar(true)}
              disabled={cerrando || loading}
              sx={{ bgcolor: "#F59E0B", "&:hover": { bgcolor: "#D97706" }, fontWeight: 700 }}
            >
              Registrar Cierre de Turno
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Confirmación de cierre */}
      <Dialog open={confirmar} onClose={() => setConfirmar(false)} maxWidth="xs" fullWidth>
        <DialogTitle fontWeight={700}>Confirmar cierre de turno</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Esta acción registrará el cierre del turno actual con los siguientes totales:
          </Typography>
          {resumen && (
            <Box sx={{ mt: 1 }}>
              {METODOS.filter(({ key }) => key !== "total_sin_metodo" || (resumen[key] ?? 0) > 0).map(({ key, label }) => (
                <Box key={key} sx={{ display: "flex", justifyContent: "space-between", py: 0.3 }}>
                  <Typography variant="body2" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{fmt(resumen[key])}</Typography>
                </Box>
              ))}
              {(resumen.num_ticket_perdido ?? 0) > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.3 }}>
                  <Typography variant="body2" color="error">Cargo ticket perdido ({resumen.num_ticket_perdido})</Typography>
                  <Typography variant="body2" fontWeight={600} color="error">{fmt(resumen.monto_ticket_perdido)}</Typography>
                </Box>
              )}
              <Divider sx={{ my: 1 }} />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography fontWeight={700}>Total general</Typography>
                <Typography fontWeight={800} color="primary">{fmt(resumen.total_general)}</Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setConfirmar(false)}>Cancelar</Button>
          <Button variant="contained" onClick={realizarCierre} disabled={cerrando}
            startIcon={cerrando ? <CircularProgress size={16} color="inherit" /> : <CheckCircle />}
            sx={{ bgcolor: "#F59E0B", "&:hover": { bgcolor: "#D97706" }, fontWeight: 700 }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar open={snackbar.open} autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default CierreCaja;
